import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ExternalLink, Globe, ImagePlus, Loader2, Newspaper, Search, Trash2, UserRound } from "lucide-react";
import { IconButton } from "@/components/ui/icon-button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useCompany } from "@/hooks/useCompany";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";

function cleanDescription(article: any) {
  return String(article?.excerpt || article?.subtitle || article?.content || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export default function PublishedArticlesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { currentCompanyId } = useCompany();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [siteFilter, setSiteFilter] = useState("all");
  const [langFilter, setLangFilter] = useState("all");

  const { data: publications = [], isLoading } = useQuery({
    queryKey: ["published-articles-overview", currentCompanyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("article_publications")
        .select("id, article_id, website_id, status, external_url, external_post_id, external_media_id, created_at, updated_at, published_at, last_synced_at, sync_error, articles(id, title, subtitle, excerpt, content, slug, language, status, featured_image_url, published_at, updated_at, categories(name), authors(name)), websites(id, name, domain, primary_language)")
        .eq("company_id", currentCompanyId)
        .eq("status", "published")
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      return data || [];
    },
    enabled: !!user && !!currentCompanyId,
  });

  const removePublication = useMutation({
    mutationFn: async (publication: any) => {
      const { error } = await supabase.functions.invoke("publish-to-wordpress", {
        body: {
          article_id: publication.article_id,
          website_id: publication.website_id,
          action: "delete",
        },
      });
      if (error) throw new Error("We could not remove this article from the live website. Please try again.");
    },
    onSuccess: () => {
      toast({ title: "Article removed", description: "The live post was removed from the website." });
      queryClient.invalidateQueries({ queryKey: ["published-articles-overview"] });
    },
    onError: (error) => {
      toast({
        title: "Removal failed",
        description: error instanceof Error ? error.message : "Please try again in a moment.",
        variant: "destructive",
      });
    },
  });

  const repairImages = useMutation({
    mutationFn: async () => {
      const targetSites = siteFilter === "all" ? sites : sites.filter(([id]) => id === siteFilter);
      if (targetSites.length === 0) return { processed: 0, repaired: 0, failed: 0, already_ok: 0 };

      const totals = { processed: 0, repaired: 0, failed: 0, already_ok: 0 };
      for (const [websiteId] of targetSites) {
        const { data, error } = await supabase.functions.invoke("publish-to-wordpress", {
          body: {
            website_id: websiteId,
            action: "repair_missing_images",
            limit: 100,
          },
        });
        if (error) throw error;
        const result = data as any;
        if (result?.error) throw new Error(result.error);
        totals.processed += Number(result?.processed || 0);
        totals.repaired += Number(result?.repaired || 0);
        totals.failed += Number(result?.failed || 0);
        totals.already_ok += Number(result?.already_ok || 0);
      }
      return totals;
    },
    onSuccess: (result) => {
      toast({
        title: "Image repair complete",
        description: `Repaired ${result.repaired}, already OK ${result.already_ok}, failed ${result.failed}.`,
      });
      queryClient.invalidateQueries({ queryKey: ["published-articles-overview"] });
    },
    onError: (error) => {
      toast({
        title: "Image repair failed",
        description: error instanceof Error ? error.message : "Please try again in a moment.",
        variant: "destructive",
      });
    },
  });

  const sites = useMemo(() => {
    const map = new Map<string, string>();
    publications.forEach((p: any) => p.websites?.id && map.set(p.websites.id, p.websites.name));
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [publications]);

  const languages = useMemo(() => {
    const set = new Set<string>();
    publications.forEach((p: any) => p.articles?.language && set.add(p.articles.language));
    return Array.from(set).sort();
  }, [publications]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return publications.filter((p: any) => {
      const article = p.articles;
      const website = p.websites;
      if (!article || !website) return false;
      if (siteFilter !== "all" && website.id !== siteFilter) return false;
      if (langFilter !== "all" && article.language !== langFilter) return false;
      if (!q) return true;
      return `${article.title} ${cleanDescription(article)} ${website.name} ${website.domain || ""}`.toLowerCase().includes(q);
    });
  }, [publications, search, siteFilter, langFilter]);

  const successCount = publications.filter((p: any) => p.status === "published").length;
  const siteCount = sites.length;

  return (
    <div className="min-h-screen bg-background p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Published Articles</h1>
          <p className="text-sm text-muted-foreground">{successCount} published records across {siteCount} site{siteCount !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search article or site…" className="h-9 w-64 pl-8 text-xs" />
          </div>
          <select value={siteFilter} onChange={(e) => setSiteFilter(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-xs text-foreground">
            <option value="all">All sites</option>
            {sites.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select value={langFilter} onChange={(e) => setLangFilter(e.target.value)} className="h-9 rounded-md border border-border bg-background px-2 text-xs text-foreground">
            <option value="all">All languages</option>
            {languages.map((lang) => <option key={lang} value={lang}>{lang.toUpperCase()}</option>)}
          </select>
          <Button
            variant="outline"
            size="sm"
            className="h-9 text-xs"
            disabled={repairImages.isPending || sites.length === 0}
            onClick={() => repairImages.mutate()}
          >
            {repairImages.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="mr-1.5 h-3.5 w-3.5" />}
            Repair missing images
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/30">
              <TableHead className="w-12">Image</TableHead>
              <TableHead>Article</TableHead>
              <TableHead className="w-56">Website</TableHead>
              <TableHead className="w-40">Author</TableHead>
              <TableHead className="w-44">Timestamps</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="py-12 text-center"><Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="py-12 text-center text-sm text-muted-foreground">No published articles match these filters.</TableCell></TableRow>
            ) : filtered.map((p: any) => {
              const article = p.articles;
              const website = p.websites;
              const description = cleanDescription(article);
              return (
                <TableRow key={p.id} className="hover:bg-muted/50">
                  <TableCell className="py-2 align-top">
                    <div className="h-10 w-10 overflow-hidden rounded-md bg-muted flex items-center justify-center">
                      {article?.featured_image_url ? <img src={article.featured_image_url} alt="" className="h-full w-full object-cover" loading="lazy" /> : <Newspaper className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 align-top">
                    <Link to={`/articles/${article.id}`} className="block text-sm font-semibold leading-snug text-foreground hover:text-primary">{article.title}</Link>
                    {description && <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">{description}</p>}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Badge variant="outline" className="h-4 px-1 text-[9px] uppercase">{article.language}</Badge>
                      <span>{article.categories?.name || "Uncategorized"}</span>
                      <Badge className="h-4 px-1 text-[9px]">{p.status}</Badge>
                      {p.external_post_id && <span>Post #{p.external_post_id}</span>}
                    </div>
                  </TableCell>
                  <TableCell className="py-2 align-top">
                    <Link to={`/websites/${website.id}`} className="flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-primary">
                      <Globe className="h-3.5 w-3.5 text-muted-foreground" /> {website.name}
                    </Link>
                    <p className="mt-0.5 truncate text-[10px] text-muted-foreground">{website.domain || "No domain"}</p>
                  </TableCell>
                  <TableCell className="py-2 align-top">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                      <UserRound className="h-3.5 w-3.5 text-muted-foreground" />
                      {article.authors?.name || "No author"}
                    </div>
                    <p className="mt-0.5 text-[10px] text-muted-foreground">Article status: {article.status}</p>
                  </TableCell>
                  <TableCell className="py-2 align-top text-[10px] text-muted-foreground">
                    <p><span className="text-foreground">Published:</span> {p.published_at ? format(new Date(p.published_at), "MMM d, HH:mm") : "—"}</p>
                    <p><span className="text-foreground">Synced:</span> {p.last_synced_at ? format(new Date(p.last_synced_at), "MMM d, HH:mm") : "—"}</p>
                    <p><span className="text-foreground">Recorded:</span> {p.created_at ? format(new Date(p.created_at), "MMM d, HH:mm") : "—"}</p>
                  </TableCell>
                  <TableCell className="py-2 align-top text-right">
                    <div className="flex justify-end gap-1">
                      {p.external_url && (
                        <a href={p.external_url} target="_blank" rel="noopener noreferrer">
                          <IconButton variant="ghost" className="h-7 w-7" label={t("actions.viewLive")}><ExternalLink className="h-3.5 w-3.5" /></IconButton>
                        </a>
                      )}
                      <IconButton
                        variant="ghost"
                        className="h-7 w-7 text-destructive hover:text-destructive"
                        label={t("actions.unpublish")}
                        disabled={removePublication.isPending}
                        onClick={() => removePublication.mutate(p)}
                      >
                        {removePublication.isPending && removePublication.variables?.id === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
